/**
 * Activation gate + Test mode card of the Test Runs screen (epic 031 task 06;
 * ADR-010 §12, README decisions 10 + 11, task 05's endpoints).
 *
 * The gate is a **warning**, never a block (the hard block is the vetoed
 * variant): the card says whether the campaign ever passed a test run and
 * carries the **Test mode** toggle (`pending | scheduled → test → pending`,
 * 409 from `active` / `completed` — the toggle is therefore disabled with a
 * hint there). Ratified: ADR-010 A23 (a).
 */

import type { ActivationCheck } from '@leads-platform/shared';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Toggle } from '../../components/ui/toggle';
import { fmtDate } from '../../lib/format';

interface GateCardProps {
  check: ActivationCheck | null;
  /** The campaign's current lifecycle status. */
  campaignStatus: string;
  /** `campaign.status === 'test'` — the toggle's on state. */
  testModeEnabled: boolean;
  onTestModeChange: (enabled: boolean) => void;
}

export function GateCard({
  check,
  campaignStatus,
  testModeEnabled,
  onTestModeChange,
}: GateCardProps) {
  const locked = campaignStatus === 'active' || campaignStatus === 'completed';
  const lockHint =
    campaignStatus === 'active'
      ? 'Test mode is a pending/scheduled state — pause the campaign first.'
      : 'A completed campaign cannot change state.';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activation gate &amp; Test mode</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {check === null ? (
          <p className="text-sm text-faint">Checking the activation gate…</p>
        ) : check.passed && check.lastPassedRun ? (
          <p className="text-sm text-muted">
            Last passed test:{' '}
            <span className="font-medium text-ink">{fmtDate(check.lastPassedRun.finishedAt)}</span>
          </p>
        ) : (
          <p className="text-sm text-muted">
            No passed test yet — activating will warn, not block.
          </p>
        )}
        <Toggle
          checked={testModeEnabled}
          disabled={locked}
          onChange={onTestModeChange}
          label="Test mode"
          hint={
            locked
              ? lockHint
              : testModeEnabled
                ? 'On — every lead the campaign captures is a test lead.'
                : 'Off — leads count normally.'
          }
        />
      </CardContent>
    </Card>
  );
}
